import casesService from "api/casesService";
import dossierService from "api/dossierService";
import AddButton from "components/AddButton";
import Button from "components/Button";
import CreateCaseModal from "components/CreateCaseModal";
import Filedossier from "components/Filedossier/Filedossier";
import ScanDocumentModal from "components/ScanDocumentModal";
import Input from "components/Input";
import ModalsManagerContext from "contexts/ModalsManager";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import AttachIcon from "assets/icons/attachments.svg?react";
import LinkIcon from "assets/icons/link.svg?react";
import { IKey } from "dto/IKey";
import useFiles from "hooks/useFiles";
import { useContext, useMemo, useRef, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useNavigate } from "react-router-dom";
import { isValidHttpUrl } from "utils/isValidHttpUrl";
import { handleCreate } from "utils/uploadFile";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import SearchCasesNew from "components/SearchCasesNew";
import DossierAnimation from "components/DossierAnimation";
import DossierFilesContext from "contexts/DossierFilesContext";
import KeysContext from "contexts/KeysContext";
import {
  CompanyKeyMissingError,
  getCompanyPublicKeyResult,
} from "utils/encryptForCompany";
import CloseIcon from "assets/icons/close.svg?react";
import GenericNavbar from "components/GenericNavbar";
import { readFileFromBase64 } from "utils/readFileFromBase64";
import * as openpgp from "openpgp";
import { logger } from "utils/logger";
import { ENotificationType, notification } from "utils/notification";
import SearchLabel from "components/SearchLabel";
import { ITag, TagType } from "dto/ITag";
import TagBadge from "components/TagBadge";
import { useTranslation } from "react-i18next";

const CreateDossierModal: FC<Props> = () => {
  const { t } = useTranslation();

  const link = window.location.href;
  const caseId = link.split("cases/")?.[1]?.split("/")?.[0] ?? undefined;
  const folderId = link.split("dossier/f/")?.[1]?.split("/")?.[0] ?? undefined;
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { modals, setModals } = useContext(ModalsManagerContext);
  const { files, addNewFile, removeFile, setFiles } = useFiles();
  const [showScan, setShowScan] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);
  const [searchedCase, setSearchedCase] = useState<ICaseSimple | null>(null);
  const { data: caseData } = casesService.useGetKeys({
    id: searchedCase?.Id ?? null,
  });

  const [tags, setTags] = useState<ITag[]>([]);

  const [externalLink, setExternalLink] = useState("");
  const [title, setTitle] = useState("");

  const [addLinke, setAddLink] = useState(false);

  const { mutate: addFileToDossier, isLoading: loadingAdd } =
    dossierService.useAddFile();
  const { mutate: addLink, isLoading: loadingLink } =
    dossierService.useAddLink();

  const key: IKey = useMemo(
    () => (caseData?.data?.[0] as IKey) ?? null,
    [caseData],
  );

  const handleClickCloseModal = () => setModals({ ...modals, hide: true });

  const handleClickToUpload = () => {
    if (!inputFile.current) return;
    inputFile.current.click();
  };

  const handleChooseFile = async (event: any) => {
    event.stopPropagation();
    event.preventDefault();
    const file = (event.target as any).files[0];
    if (!file) return;

    addNewFile(file);
  };

  const handleCreateLink = () => {
    addLink(
      {
        CaseId: searchedCase?.Id,
        ParentId: folderId,
        Label: title,
        Url: externalLink,
        TagIds: tags.map((t) => t.Id),
      },
      {
        onSuccess: (resp) => {
          setTitle("");
          setExternalLink("");
          if (searchedCase) {
            if (folderId) {
              navigate(`/cases/${searchedCase!.Id}/dossier/f/${folderId}`);
            } else {
              navigate(`/cases/${searchedCase!.Id}/dossier`);
            }
          }
          resp.data.tagIds = [];
          console.log(resp.data);
          //@ts-ignore
          setDossierFiles([...dossierFiles, ...resp.data.Items]);
          handleClickCloseModal();
        },
      },
    );
  };

  // Starting process of upload after click on button

  const [actualFile, setActualFile] = useState(0);
  const [actualProgress, setActualProgress] = useState<number | null>(null);
  const { dossierFiles, setDossierFiles } = useContext(DossierFilesContext);
  const { keys, setKeys } = useContext(KeysContext);

  const handleCreatee = async () => {
    setIsLoading(true);

    if (!!externalLink.length && isValidHttpUrl(externalLink)) {
      handleCreateLink();
      return;
    }

    let masterKey: openpgp.key.KeyResult = null;

    if (searchedCase) {
      const casePublicKeyData = await readFileFromBase64(
        key.PublicKey,
        "application/other",
      );

      masterKey = await openpgp.key.read(new Uint8Array(casePublicKeyData));
    } else {
      // Element nieprzypisany do sprawy — klucz obiektu szyfrujemy kluczem publicznym
      // FIRMY (docs/model-kluczy-firmowych.md, inwariant I1), a nie kluczem profilu ani
      // kluczem intermediate użytkownika.
      try {
        masterKey = await getCompanyPublicKeyResult();
      } catch (error) {
        // FAIL-CLOSED: bez klucza firmy plik trafiłby na Dysk nieczytelny dla reszty
        // kancelarii — przerywamy z jawnym komunikatem.
        setIsLoading(false);
        notification({
          title: t("errors.occurred"),
          text:
            error instanceof CompanyKeyMissingError
              ? error.message
              : t("errors.somethingWentWrong"),
          type: ENotificationType.ERROR,
        });
        return;
      }
    }

    for (const file of files) {
      const r = await handleCreate(
        [file],
        searchedCase?.Id,
        masterKey,
        true,
        true,
        undefined,
        undefined,
        (fileIndex: number, progress: number) => {
          setActualProgress(progress);
        },
      );

      if (!r) {
        return;
      }

      addFileToDossier(
        {
          CaseId: searchedCase?.Id,
          ParentId: folderId,
          Files: [
            //@ts-ignore
            {
              KeyId: r[0].keyId,
              Uri: r[0].uri,
            },
          ],
          TagIds: tags.map((t) => t.Id),
        },
        {
          onSuccess: (resp) => {
            setActualFile((p) => p + 1);
            setActualProgress(0);
            setIsLoading(false);

            const k: any = resp.data.Keys.flat();
            const ok: any = resp.data.ObjectKeys.flat();

            setKeys([...keys, k, ok].flat());
            setDossierFiles([...resp.data.Items, ...dossierFiles]);

            logger.log("Create success", resp);
          },
          onError: () => setIsLoading(false),
        },
      );
    }

    if (searchedCase) {
      if (folderId) {
        navigate(`/cases/${searchedCase!.Id}/dossier/f/${folderId}`);
      } else {
        navigate(`/cases/${searchedCase!.Id}/dossier`);
      }
    }
    handleClickCloseModal();
  };

  const handleRemove = (index: number) => removeFile(index);

  const Attachments = () => {
    if (externalLink.length) {
      return <></>;
    }

    return (
      <>
        <AddButton
          className={styles.add}
          onClick={() => setShowScan(true)}
          text={t("scanner.scan")}
        />
        {!files.length ? (
          <FileUploader
            multiple={true}
            handleChange={async (f: any) => {
              const newFiles = Array.from(f).map((f) => f) as File[];
              setFiles([...files, newFiles].flat());
            }}
            classes={styles.drop}
            dropMessageStyle={{
              opacity: 0,
            }}
            name="file"
            children={
              <div className={styles.emptyAttachments}>
                {/* <NoFileIcon className={styles.noFiles} /> */}
                <DossierAnimation className={styles.noFiles} />
                <div className={styles.emptyButtons}>
                  {t("forms.dragAndDrop")}
                  <div className={styles.buttonCTA}>
                    {t("forms.chooseFromDisk")}
                  </div>
                </div>
              </div>
            }
          />
        ) : (
          <div className={styles.attachmentsWrapper}>
            <AddButton
              className={styles.add}
              onClick={handleClickToUpload}
              text={t("forms.addFile")}
            />
            <div className={styles.attachments}>
              {files.map((file: File, index: number) => (
                <Filedossier
                  key={index}
                  index={index}
                  handleRemove={handleRemove}
                  file={file}
                  actualFile={actualFile}
                  actualProgress={actualProgress}
                />
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  const [createCase, setCreateCase] = useState(false);

  const [showErrors, setShowErrors] = useState({ case: false, files: false });

  const isButtonActive = () => {
    const isSendingFilesOrLink = loadingAdd || loadingLink;

    if (isSendingFilesOrLink) {
      return true;
    }

    if (externalLink.length) {
      if (isValidHttpUrl(externalLink)) {
        return false;
      }
      return true;
    }

    if (!externalLink.length && !!files.length) {
      return false;
    }

    return true;
  };

  const handleError = () => {
    let payload = {
      case: false,
      files: false,
    };

    if (!searchedCase) {
      payload = { ...payload, case: true };
    }

    if (!externalLink.length || !!files.length) {
      payload = { ...payload, files: true };
    }

    setShowErrors(payload);
  };

  const addNewTag = (tag: ITag | null) => {
    const isTag = tags.find((etag) => etag.Name === tag!.Name);
    if (isTag) return;
    setTags([...tags, tag!]);
  };

  const handleRemoveTag = (tag: ITag) =>
    setTags(tags.filter((etag) => tag.Name !== etag.Name));

  if (createCase) {
    return <CreateCaseModal />;
  }

  const refWrapper = useRef(null);

  return (
    <ModalLayout
      dock={DockPosition.DISABLED}
      off={true}
      customX={8}
      name="createDossier"
      isAnimation
      customY={0}
      ignore
      refWrapper={refWrapper}
      fullHeight={true}
      disableMove={true}
    >
      <div className={styles.wrapper}>
        {showScan && (
          <ScanDocumentModal
            handleClose={() => setShowScan(false)}
            onComplete={(pdf) => addNewFile(pdf)}
          />
        )}
        <div className={styles.title}>
          {t("documents.newElement")}
          <CloseIcon
            onClick={handleClickCloseModal}
            className={styles.closeIcon}
          />
        </div>
        <div className={styles.content}>
          <GenericNavbar
            selected={addLinke ? 1 : 0}
            firstElement={
              <>
                <AttachIcon /> {t("documents.file")}
              </>
            }
            secondElement={
              <>
                <LinkIcon /> Link
              </>
            }
            onClickFirstElement={() => {
              setAddLink(false);
            }}
            onClickSecondElement={() => {
              setAddLink(true);
            }}
          />

          <div className={styles.case}>
            <SearchCasesNew
              isError={showErrors.case ? t("cases.chooseCase") : ""}
              setAddNewEntity={setCreateCase}
              defaultValue={caseId ? caseId : undefined}
              onChoose={(simpleCase) => setSearchedCase(simpleCase)}
            />
          </div>

          <div className={styles.tags}>
            <div className={styles.row1}>
              <SearchLabel
                label={t("common.label")}
                type={TagType.EditorDocuments}
                onChoose={addNewTag}
              />
            </div>
            <div className={styles.row2}>
              {!!tags.length && (
                <>
                  {tags.map((tag) => (
                    <TagBadge
                      useBigChip
                      key={tag.Id}
                      tag={tag}
                      onRemove={handleRemoveTag}
                    />
                  ))}
                </>
              )}
            </div>
          </div>

          {addLinke && (
            <div className={styles.link}>
              <Input
                label={t("forms.title")}
                onChange={(t) => setTitle(t)}
                value={title}
              />
              <Input
                label="Link"
                onChange={(t) => setExternalLink(t)}
                value={externalLink}
              />
            </div>
          )}
          {!addLinke && <Attachments />}
          {showErrors.files && (
            <div className={styles.error}>
              {t("documents.chooseFilesOrLink")}
            </div>
          )}
        </div>
        <div className={styles.footer}>
          <Button
            className={styles.button}
            onClick={handleCreatee}
            text={t("forms.addElement")}
            loading={isLoading}
            handleError={handleError}
            disabled={isButtonActive()}
          />
        </div>
      </div>
      <input
        type="file"
        id="file"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={handleChooseFile}
      />
    </ModalLayout>
  );
};

export default CreateDossierModal;
